// AnnouncementsPanel (shown as "Notifications") — push a message straight to
// phones: the whole school, all parents, staff, drivers, parents of chosen
// classes, or one student's parents. Push is free and instant (no SMS cost),
// and the guardian/staff apps show it as a popup.
import { useEffect, useState } from 'react';
import { Send, Smartphone } from 'lucide-react';
import { getAnnouncements, createAnnouncement, getNotificationReach, getClasses } from '../api/client';
import Button from '../ui/Button';
import { Card, CardHeader } from '../ui/Card';
import { Field, Msg, errText, inputCls, theadRowCls, EmptyRow } from '../ui/Form';
import StudentPicker from './StudentPicker';
import { fmtDate, fmtTime } from '../ui/format';

const AUDIENCES = [
  { key: 'all',       label: 'Everyone' },
  { key: 'guardians', label: 'All parents' },
  { key: 'classes',   label: 'Parents of classes' },
  { key: 'student',   label: 'One student\'s parent' },
  { key: 'staff',     label: 'All staff' },
  { key: 'drivers',   label: 'All drivers' },
];

const TEMPLATES = [
  { title: 'School closed tomorrow', message: 'School will remain closed tomorrow due to bad weather. Classes resume the day after.' },
  { title: 'Parent-teacher meeting', message: 'PTM is on Saturday from 10 am to 1 pm. Please meet your child\'s class teacher.' },
  { title: 'Fee reminder', message: 'Kindly clear this month\'s fee before the due date to avoid a late fine.' },
  { title: 'Exam schedule', message: 'Half-yearly exams start next Monday. The date sheet has been shared with students.' },
];

export default function AnnouncementsPanel() {
  const [history,  setHistory]  = useState([]);
  const [reach,    setReach]    = useState(null);
  const [classes,  setClasses]  = useState([]);
  const [title,    setTitle]    = useState('');
  const [message,  setMessage]  = useState('');
  const [audience, setAudience] = useState('all');
  const [classIds, setClassIds] = useState([]);
  const [student,  setStudent]  = useState(null);
  const [sending,  setSending]  = useState(false);
  const [msg,      setMsg]      = useState('');

  useEffect(() => {
    load();
    getNotificationReach().then(setReach).catch(() => {});
    getClasses().then(setClasses).catch(() => {});
  }, []);

  async function load() {
    try { setHistory(await getAnnouncements()); } catch { setMsg('Failed to load history'); }
  }

  function toggleClass(id) {
    setClassIds(ids => ids.includes(id) ? ids.filter(x => x !== id) : [...ids, id]);
  }

  async function send(e) {
    e.preventDefault();
    if (audience === 'classes' && !classIds.length) return setMsg('Error: pick at least one class');
    if (audience === 'student' && !student) return setMsg('Error: pick a student');
    setSending(true); setMsg('');
    try {
      const r = await createAnnouncement({
        title, message, audience,
        class_ids: audience === 'classes' ? classIds : undefined,
        student_id: audience === 'student' ? student.id : undefined,
      });
      const missing = r.without_app ? ` · ${r.without_app} of ${r.people} don't have the app yet` : '';
      setMsg(`Sent to ${r.recipient_count} phone${r.recipient_count === 1 ? '' : 's'} (${r.audience_label}) ✅${missing}`);
      setTitle(''); setMessage(''); load();
    } catch (err) {
      setMsg(errText(err));
    } finally { setSending(false); }
  }

  return (
    <div>
      <h2 className="font-display font-extrabold text-xl text-ink mb-1">Notifications</h2>
      <p className="text-sm text-ink-soft mb-4">Free, instant push to the parent, staff and driver apps — no SMS charges.</p>

      {reach && (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(180px,1fr))] gap-3 mb-5">
          <ReachTile label="Parents with the app" have={reach.guardians_app} total={reach.guardians} />
          <ReachTile label="Staff with the app" have={reach.staff_app} total={reach.staff} />
          <ReachTile label="Drivers with the app" have={reach.drivers_app} total={reach.drivers} />
        </div>
      )}

      <div className="grid grid-cols-[minmax(320px,520px)_1fr] gap-5 items-start max-[1000px]:grid-cols-1">
        <Card className="p-5">
          <h3 className="font-display font-bold text-[15px] text-ink mb-3">New notification</h3>
          <form onSubmit={send} className="flex flex-col gap-3">
            <div className="flex flex-col gap-1 text-xs font-bold text-ink-soft">
              Send to
              <div className="flex flex-wrap gap-1.5" role="group" aria-label="Send to">
                {AUDIENCES.map(a => (
                  <button type="button" key={a.key} onClick={() => setAudience(a.key)}
                    className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-colors
                      ${audience === a.key ? 'bg-transit text-white border-transit' : 'bg-paper-raised text-ink border-line hover:border-transit'}`}>
                    {a.label}
                  </button>
                ))}
              </div>
            </div>

            {audience === 'classes' && (
              <div className="flex flex-wrap gap-1.5 bg-paper rounded-s p-2.5">
                {classes.map(c => (
                  <label key={c.id} className={`flex items-center gap-1.5 px-2.5 py-1 rounded-s text-sm font-semibold cursor-pointer
                    ${classIds.includes(c.id) ? 'bg-transit-tint text-transit-deep' : 'text-ink'}`}>
                    <input type="checkbox" checked={classIds.includes(c.id)} onChange={() => toggleClass(c.id)} className="accent-[var(--transit)]" />
                    {c.name}
                  </label>
                ))}
                {!classes.length && <span className="text-sm text-ink-soft">No classes yet</span>}
              </div>
            )}
            {audience === 'student' && <StudentPicker value={student} onChange={setStudent} />}

            <Field label="Title">
              <input required maxLength={150} className={inputCls} value={title} onChange={e => setTitle(e.target.value)} />
            </Field>
            <Field label="Message">
              <textarea required rows={4} className={inputCls} value={message} onChange={e => setMessage(e.target.value)} />
            </Field>
            <div className="flex flex-wrap gap-1.5">
              <span className="text-xs font-bold text-ink-soft mr-1 self-center">Templates:</span>
              {TEMPLATES.map(t => (
                <button type="button" key={t.title} onClick={() => { setTitle(t.title); setMessage(t.message); }}
                  className="text-xs font-semibold text-transit-deep bg-transit-tint rounded-full px-2.5 py-1 hover:underline">{t.title}</button>
              ))}
            </div>
            <Button type="submit" disabled={sending} className="flex items-center justify-center gap-1.5 w-fit">
              <Send size={14} /> {sending ? 'Sending…' : 'Send now'}
            </Button>
            <Msg msg={msg} />
          </form>
        </Card>

        <Card>
          <CardHeader title={`Sent (${history.length})`} />
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className={theadRowCls}>
                  <th className="px-4 py-2.5">Sent</th><th className="px-4 py-2.5">To</th>
                  <th className="px-4 py-2.5">Message</th><th className="px-4 py-2.5">Phones</th>
                </tr>
              </thead>
              <tbody>
                {history.map(a => (
                  <tr key={a.id} className="border-t border-line-soft align-top">
                    <td className="px-4 py-3 whitespace-nowrap text-ink-soft">{fmtDate(a.sent_at)}<span className="block text-xs">{fmtTime(a.sent_at)}</span></td>
                    <td className="px-4 py-3 font-semibold">{a.audience_label || 'Everyone'}</td>
                    <td className="px-4 py-3"><b className="text-ink">{a.title}</b><span className="block text-ink-soft">{a.message}</span></td>
                    <td className="px-4 py-3 tabular-nums">{a.recipient_count ?? '—'}</td>
                  </tr>
                ))}
                {!history.length && <EmptyRow cols={4}>Nothing sent yet</EmptyRow>}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
}

function ReachTile({ label, have, total }) {
  const pct = total ? Math.round((have / total) * 100) : 0;
  return (
    <div className="bg-paper-raised border border-line rounded-m p-4">
      <span className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-ink-soft mb-1.5"><Smartphone size={12} /> {label}</span>
      <span className="font-display font-extrabold text-2xl text-ink tabular-nums">{have}<span className="text-base text-ink-soft"> / {total}</span></span>
      <div className="h-1.5 bg-line-soft rounded-full mt-2 overflow-hidden"><div className="h-full bg-transit rounded-full" style={{ width: `${pct}%` }} /></div>
    </div>
  );
}
